// Scattered minor-body debris — meteoroids, asteroids and a comet
// drifting and tumbling through the solar system. The models are
// CC BY 4.0 Sketchfab assets (attributed on /credits):
//   meteorite.glb        — "Meteorite" by Hailphilly
//   asteroid-01.glb      — "Asteroid 01" by exabyte
//   asteroid-02.glb      — "Asteroid" by Cyber Kriss
//   asteroid-bennu.glb   — "Asteroid 101955 Bennu" by Geo_Monkey
//   comet.glb            — "Comet" by organ collector
//   asteroid-field-*.glb — "Asteroid Field (100x)" by Sereib
//
// Each body sits on a slow, slightly-inclined circular orbit around the
// Sun (origin) so it stays in the system, plus a random local tumble.
// Orbit advances on sim-scaled time (so it follows the time-speed
// slider like the planets); tumble runs on wall-clock time so bodies
// never look frozen. To scatter a new model, add one entry to BODIES or
// FIELDS — the loader, placement and per-frame motion handle the rest.

import * as THREE from 'three';
import { shrinkModelTextures } from './mobiledevice.js';
import { uploadInBands } from './bandupload.js';
import { compileForSceneAsync, SLOW_COMPILE_LIMIT_MS } from './gpuwarm.js';

// Scene-scale reference (dramatised orbits): Mercury ~70, Earth ~140,
// Mars ~200, Jupiter ~320, Neptune ~640. The debris is meant to read as
// sparse, barely-visible minor bodies far out in the dark — NOT crowding
// the inner system — so the scatter shell sits WELL beyond Neptune,
// spread through a thick spherical volume (a scattered-disc / Oort-like
// cloud) so they're well separated and only show up when you venture out.
// Neptune sits at ~640. The HUD reads 1 AU = 150 units, so this shell
// (5000-14000) is roughly 33-93 AU — true Kuiper-belt / scattered-disc
// depth, FAR beyond every planet. At cruise fly-speed it takes the best
// part of a minute of travel before you reach the first one, and they
// stay sparse and barely visible until you're right out in the dark,
// which is exactly the brief.
const R_INNER = 5000;
const R_OUTER = 14000;
const SPREAD = 0.6;       // 0 = flat ecliptic disc, ~1 = full sphere; thick cloud

// Individual drifting bodies. size = [min, max] longest-dimension in
// scene units (Earth's radius is 2.5 for reference, so a 1-5 unit rock
// reads as a small-but-visible body at orbital distance).
const BODIES = [
  { url: '/models/meteorite.glb',      count: 12, size: [1.5, 4.0] },
  { url: '/models/asteroid-01.glb',    count: 10, size: [2.0, 6.0] },
  { url: '/models/asteroid-02.glb',    count: 10, size: [2.0, 6.0] },
  { url: '/models/asteroid-bennu.glb', count: 6,  size: [3.0, 8.0] },
  { url: '/models/comet.glb',          count: 3,  size: [4.0, 9.0] },
];

// Denser pre-built clusters (100 rocks each in the source GLB). One of
// each, placed as a thick pocket out near / beyond the main belt.
const FIELDS = [
  { url: '/models/asteroid-field-med.glb', span: 60 },
  { url: '/models/asteroid-field-low.glb', span: 60 },
];

const MAX_ORBIT_STEP = 0.04; // rad/frame clamp so high time-speed can't fling bodies

let root = null;
let started = false;
const bodies = []; // { mesh, pos, orbitAxis, orbitSpeed, tumbleAxis, tumbleSpeed }

function rand(a, b) { return a + Math.random() * (b - a); }

let maxTextureSize = 0;
// { renderer, camera } to compile each model's shaders before it is shown.
let gpu = null;

// A model joins the scene once its programs are ready: drawn sooner, the
// frame waited on the driver for each, half a second apiece where shaders
// compile slowly (Edge on Windows on a first visit), six times over.
async function compiled(src, scene) {
  if (gpu) await compileForSceneAsync(gpu.renderer, src, gpu.camera, scene, SLOW_COMPILE_LIMIT_MS);
}

async function loadGltfScene(url) {
  const [{ GLTFLoader }, meshoptModule] = await Promise.all([
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/libs/meshopt_decoder.module.js'),
  ]);
  const MeshoptDecoder = meshoptModule.MeshoptDecoder || meshoptModule.default || meshoptModule;
  if (MeshoptDecoder && MeshoptDecoder.ready) {
    try { await MeshoptDecoder.ready; } catch (err) { console.warn('[debris] meshopt ready failed', err); }
  }
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const gltf = await loader.loadAsync(url);
  if (maxTextureSize) shrinkModelTextures(gltf.scene, maxTextureSize);
  // Its maps go to the GPU in bands before it joins the scene (where the
  // viewer sends maps so, src/bandupload.js): sent whole when a body first
  // came into view they cost 16 ms each, in the middle of a drag.
  const maps = new Set();
  gltf.scene.traverse((node) => {
    const materials = !node.material ? [] : Array.isArray(node.material) ? node.material : [node.material];
    for (const m of materials) for (const value of Object.values(m)) if (value && value.isTexture) maps.add(value);
  });
  await Promise.all([...maps].map((map) => uploadInBands(null, map, null)));
  // Debris is decorative: no shadow casting, a faint emissive floor so
  // bodies in the dim outer system never read as pure black, and not
  // raycast-pickable (they're never added to the clickable set).
  const seen = new Set();
  gltf.scene.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = false;
    node.receiveShadow = false;
    node.frustumCulled = true;
    const m = node.material;
    if (!m || seen.has(m.uuid)) return;
    seen.add(m.uuid);
    if (m.emissive && m.emissiveIntensity !== undefined && m.emissive.r + m.emissive.g + m.emissive.b < 0.02) {
      m.emissive = new THREE.Color(0x0a0a0c);
      m.emissiveIntensity = 1.0;
    }
  });
  return gltf.scene;
}

// Clone + scale a source scene to a target longest-dimension, recentred
// so the returned wrapper's origin is the body's geometric centre.
function makeInstance(src, targetLongest) {
  const obj = src.clone(true);
  const box = new THREE.Box3().setFromObject(obj);
  const size = new THREE.Vector3();
  box.getSize(size);
  const longest = Math.max(size.x, size.y, size.z) || 1;
  const fit = targetLongest / longest;
  obj.scale.setScalar(fit);
  const center = new THREE.Vector3();
  box.getCenter(center);
  obj.position.copy(center.multiplyScalar(-fit));
  const wrapper = new THREE.Group();
  wrapper.add(obj);
  return wrapper;
}

// A point on a slightly-inclined orbit shell. flatten controls how far
// off the ecliptic bodies stray (small = belt-like disc, larger =
// scattered cloud).
function shellPosition(rInner, rOuter, flatten) {
  const r = rand(rInner, rOuter);
  const theta = Math.random() * Math.PI * 2;
  const incl = (Math.random() - 0.5) * 2 * flatten;
  const rp = r * Math.cos(incl);
  return new THREE.Vector3(Math.cos(theta) * rp, r * Math.sin(incl), Math.sin(theta) * rp);
}

function addBody(mesh, pos, opts = {}) {
  mesh.position.copy(pos);
  mesh.rotation.set(rand(0, 6.283), rand(0, 6.283), rand(0, 6.283));
  root.add(mesh);
  const radius = Math.max(pos.length(), 1e-3);
  // Keplerian-ish: inner bodies orbit faster. Base rate is gentle so
  // it reads as a slow drift at default time-speed.
  const speedScale = Math.sqrt(240 / radius);
  bodies.push({
    mesh,
    pos: pos.clone(),
    orbitAxis: new THREE.Vector3(rand(-0.16, 0.16), 1, rand(-0.16, 0.16)).normalize(),
    orbitSpeed: (Math.random() < 0.5 ? 1 : -1) * rand(opts.minOrbit ?? 0.010, opts.maxOrbit ?? 0.045) * speedScale,
    tumbleAxis: new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)).normalize(),
    tumbleSpeed: opts.tumble ?? rand(0.12, 0.6),
  });
}

// Bumped by dispose(), so a build still loading stops adding to the old field.
let generation = 0;

async function build(scene) {
  const gen = generation;
  root = new THREE.Group();
  root.name = 'debrisField';
  scene.add(root);

  for (const def of BODIES) {
    let src;
    try { src = await loadGltfScene(def.url); }
    catch (err) { console.warn('[debris] failed to load', def.url, err); continue; }
    await compiled(src, scene);
    if (gen !== generation) return;
    for (let i = 0; i < def.count; i++) {
      addBody(makeInstance(src, rand(def.size[0], def.size[1])), shellPosition(R_INNER, R_OUTER, SPREAD));
    }
  }

  for (const def of FIELDS) {
    let src;
    try { src = await loadGltfScene(def.url); }
    catch (err) { console.warn('[debris] failed to load', def.url, err); continue; }
    await compiled(src, scene);
    if (gen !== generation) return;
    // Fields sit in the same deep-space shell, tumbling very slowly so
    // the whole cluster doesn't spin like a top.
    const pos = shellPosition(R_INNER, R_OUTER, SPREAD * 0.85);
    addBody(makeInstance(src, def.span), pos, { minOrbit: 0.004, maxOrbit: 0.012, tumble: rand(0.02, 0.05) });
  }
}

// options.maxTextureSize: the models' maps shrunk to this long edge (phones);
// options.renderer and options.camera: compile each model before showing it.
export function createDebrisField(scene, options = {}) {
  if (started) return debrisHandle;
  started = true;
  maxTextureSize = options.maxTextureSize || 0;
  gpu = options.renderer && options.camera ? { renderer: options.renderer, camera: options.camera } : null;
  build(scene).catch((err) => console.warn('[debris] build failed', err));
  return debrisHandle;
}

const debrisHandle = {
  update(scaledDelta, delta) {
    if (!root || bodies.length === 0) return;
    for (let i = 0; i < bodies.length; i++) {
      const b = bodies[i];
      const step = THREE.MathUtils.clamp(b.orbitSpeed * scaledDelta, -MAX_ORBIT_STEP, MAX_ORBIT_STEP);
      b.pos.applyAxisAngle(b.orbitAxis, step);
      b.mesh.position.copy(b.pos);
      b.mesh.rotateOnAxis(b.tumbleAxis, b.tumbleSpeed * delta);
    }
  },
  get root() { return root; },
  // Every model freed (a phone back in the inner system); createDebrisField
  // builds the field again.
  dispose() {
    generation++;
    if (root) {
      root.parent?.remove(root);
      const owned = new Set();
      root.traverse((node) => {
        if (node.geometry) owned.add(node.geometry);
        const materials = !node.material ? [] : Array.isArray(node.material) ? node.material : [node.material];
        for (const m of materials) {
          owned.add(m);
          for (const value of Object.values(m)) if (value && value.isTexture) owned.add(value);
        }
      });
      owned.forEach((o) => o.dispose());
    }
    root = null;
    bodies.length = 0;
    started = false;
  },
};
